/**
 * PayPal one-off checkout button (Orders API v2).
 *
 * Uses the official PayPal JS SDK Buttons, which open PayPal's own popup —
 * sidestepping the iframe X-Frame-Options problem entirely (no top-frame
 * redirect needed). Order creation + capture are both server-side:
 *   createOrder  → POST /api/payments/order   (server prices the order)
 *   onApprove    → POST /api/payments/paypal/capture
 * On a COMPLETED capture we call onPaid() so the caller can refresh access.
 */
import { useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { createPaypalOrder, capturePaypalOrder } from "@/lib/config";
import { trackPurchase } from "@/lib/metaPixel";

export default function PayPalCheckoutButton({ clientId, productSlug = "brief", resourceRef, onPaid }) {
  const [working, setWorking] = useState(false);

  if (!clientId) {
    return (
      <div
        data-testid="paypal-misconfigured"
        className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-300"
      >
        <AlertCircle className="h-4 w-4" /> PayPal isn't configured yet.
      </div>
    );
  }

  return (
    <div data-testid="paypal-checkout" className="relative">
      {working && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-slate-900/60 rounded-md">
          <Loader2 className="h-5 w-5 animate-spin text-slate-600 dark:text-slate-300" />
        </div>
      )}
      <PayPalScriptProvider options={{ clientId, currency: "USD", intent: "capture" }}>
        <PayPalButtons
          style={{ layout: "vertical", shape: "pill", color: "gold", label: "pay", height: 44 }}
          fundingSource={undefined}
          createOrder={async () => {
            try {
              const res = await createPaypalOrder({ product_slug: productSlug, resource_ref: resourceRef });
              if (!res?.order_id) throw new Error("No order id returned");
              return res.order_id;
            } catch (e) {
              console.error("[paypal] createOrder failed", e);
              toast.error(e?.response?.data?.detail || "Could not start PayPal checkout.");
              throw e;
            }
          }}
          onApprove={async (data) => {
            setWorking(true);
            try {
              const res = await capturePaypalOrder(data.orderID);
              if (res?.status === "paid") {
                if (!res.duplicate) {
                  trackPurchase(res.amount, res.currency || "USD", {
                    content_type: "product",
                    content_ids: res.product ? [res.product] : [],
                    num_items: 1,
                  });
                }
                toast.success("Payment received! Unlocking your plan…");
                onPaid?.(res);
              } else {
                toast.message(`Payment status: ${res?.status || "pending"}. We'll update shortly.`);
                onPaid?.(res);
              }
            } catch (e) {
              console.error("[paypal] capture failed", e);
              toast.error(e?.response?.data?.detail || "Payment captured by PayPal but confirmation failed. Contact support if charged.");
            } finally {
              setWorking(false);
            }
          }}
          onCancel={() => toast.message("PayPal checkout cancelled.")}
          onError={(err) => {
            console.error("[paypal] sdk error", err);
            toast.error("PayPal hit an error. Please try again or use another payment method.");
          }}
        />
      </PayPalScriptProvider>
    </div>
  );
}
